import { useEffect } from 'react';
import { Button, InlineLoading } from '@carbon/react';
import { Copy } from '@carbon/react/icons';
import { useAtom, useAtomValue } from 'jotai';
import { useHistory } from 'react-router-dom';
import routes from '~/routes';
import { ScenarioFormLayout } from 'components/scenario-form-layout';
import type { ScenarioNavItem } from 'components/scenario-nav/types';
import type { ScenarioMapFeature } from 'components/scenario-map/types';
import type { AreaSelection, TaxScenarioFormData } from './types';
import { ScenarioFormProvider, useScenarioForm } from './contexts';
import {
  areaGeometriesAtom,
  categoriesLoadableAtom,
  unitTypesLoadableAtom,
  cloneScenarioLoadableAtom,
  toastAtom,
  taxMapConfigAtom,
} from './atoms';
import { ScenarioGlobalConfigs } from 'components/scenario-global-configs';
import { ScenarioAreaConfigs } from 'components/scenario-area-configs';
import { TaxRatesTable } from './tax-rates-table';
import {
  GLOBAL_SELECTION_ID,
  getAreaDisplayName,
  getAreaBadge,
  type FormMode,
} from './utils';
import { getMemoizedOverlappingAreaIds } from 'utils/geometry-overlap';
import styles from './scenario-form.module.scss';

interface ScenarioFormProps {
  initialValues: TaxScenarioFormData;
  mode: FormMode;
  scenarioId?: string;
  activityUrl?: string;
}

/**
 * Main form component for creating/editing tax scenarios.
 * @param {ScenarioFormProps} props - The form props
 * @return {JSX.Element} The scenario form component
 */
export function ScenarioForm({
  initialValues,
  mode,
  scenarioId,
  activityUrl,
}: ScenarioFormProps): JSX.Element {
  const geometries = useAtomValue(areaGeometriesAtom);

  return (
    <ScenarioFormProvider
      initialValues={initialValues}
      mode={mode}
      scenarioId={scenarioId}
      geometries={geometries}
    >
      <ScenarioFormContent scenarioId={scenarioId} activityUrl={activityUrl} />
    </ScenarioFormProvider>
  );
}

function ScenarioFormContent({
  scenarioId,
}: {
  scenarioId?: string;
  activityUrl?: string;
}): JSX.Element {
  const {
    form,
    mode,
    selectedId,
    setSelectedId,
    addArea,
    removeArea,
    handleSave,
    isSaving,
  } = useScenarioForm();

  const geometries = useAtomValue(areaGeometriesAtom);
  const [toast, setToast] = useAtom(toastAtom);
  const [cloneState, triggerClone] = useAtom(cloneScenarioLoadableAtom);
  const history = useHistory();

  const categoriesState = useAtomValue(categoriesLoadableAtom);
  const unitTypesState = useAtomValue(unitTypesLoadableAtom);
  const isCloning = cloneState.state === 'loading';

  useEffect(() => {
    if (categoriesState.state === 'hasError') {
      setToast({ message: 'Failed to load area categories', type: 'error' });
    }
  }, [categoriesState.state, setToast]);

  useEffect(() => {
    if (unitTypesState.state === 'hasError') {
      setToast({ message: 'Failed to load unit types', type: 'error' });
    }
  }, [unitTypesState.state, setToast]);

  useEffect(() => {
    if (Object.keys(geometries).length > 0) {
      form.validate('change');
    }
  }, [geometries, form]);

  const handleClone = () => {
    if (!scenarioId) return;
    triggerClone(Number(scenarioId))
      .then((data) => {
        history.push(data.href);
      })
      .catch(() => {});
  };

  const handleCancel = () => {
    history.push(routes.taxScenariosPath);
  };

  const scenarioName = form.getFieldValue('global.name');

  return (
    <form.Subscribe
      selector={(state) => {
        const { areas } = state.values;
        const overlapping = getMemoizedOverlappingAreaIds(areas, geometries);

        const navItems: ScenarioNavItem[] = areas.map(
          (area: AreaSelection) => ({
            id: area.id,
            name: getAreaDisplayName(area),
            badge: getAreaBadge(area),
            hasOverlap: overlapping.has(area.id),
            isNew: area.status === 'new',
          })
        );

        const mapFeatures: ScenarioMapFeature[] = areas
          .filter((a: AreaSelection) => geometries[a.id])
          .map((a: AreaSelection) => ({
            id: a.id,
            geometry: geometries[a.id],
            type: 'polygon' as const,
          }));

        const currentArea =
          selectedId !== GLOBAL_SELECTION_ID
            ? areas.find((a: AreaSelection) => a.id === selectedId)
            : undefined;

        return {
          navItems,
          mapFeatures,
          contentTitle: currentArea
            ? getAreaDisplayName(currentArea)
            : 'Scenario Details',
          canSubmit: state.canSubmit,
          isDirty: state.isDirty,
          hasOverlap: overlapping.size > 0,
          selectedAreaOverlap:
            currentArea && overlapping.has(currentArea.id)
              ? 'Overlaps with an existing area'
              : undefined,
        };
      }}
    >
      {({
        navItems,
        mapFeatures,
        contentTitle,
        canSubmit,
        isDirty,
        hasOverlap,
        selectedAreaOverlap,
      }) => {
        const canSave =
          (mode === 'create' ? canSubmit : canSubmit && isDirty) && !hasOverlap;

        return (
          <ScenarioFormLayout
            title={
              mode === 'create'
                ? 'Create Tax Scenario'
                : scenarioName || 'Edit Tax Scenario'
            }
            mapFeatures={mapFeatures}
            mapFocusedId={typeof selectedId === 'number' ? selectedId : null}
            mapConfigAtom={taxMapConfigAtom}
            navItems={navItems}
            navSelectedId={selectedId}
            onNavSelect={setSelectedId}
            onNavAdd={addArea}
            onNavDelete={removeArea}
            navGlobalLabel="Scenario Details"
            contentTitle={contentTitle}
            overlapError={selectedAreaOverlap}
            headerActions={
              <div className={styles.headerActions}>
                {mode === 'edit' && (
                  <Button
                    kind="tertiary"
                    size="md"
                    renderIcon={Copy}
                    disabled={isCloning}
                    onClick={handleClone}
                  >
                    {isCloning ? <InlineLoading /> : 'Clone Scenario'}
                  </Button>
                )}
                <Button kind="secondary" size="md" onClick={handleCancel}>
                  Cancel
                </Button>
                <Button
                  kind="primary"
                  size="md"
                  disabled={!canSave || isSaving}
                  onClick={handleSave}
                >
                  {isSaving ? 'Saving...' : 'Save Scenario'}
                </Button>
              </div>
            }
            toast={toast}
            onToastClose={() => setToast(null)}
          >
            {selectedId === GLOBAL_SELECTION_ID ? (
              <ScenarioGlobalConfigs form={form} showExpirationDate />
            ) : (
              <form.Subscribe
                selector={(state) =>
                  state.values.areas.findIndex(
                    (a: AreaSelection) => a.id === selectedId
                  )
                }
              >
                {(areaIndex) => {
                  if (areaIndex < 0) return null;
                  return (
                    <div className={styles.areaSections}>
                      <ScenarioAreaConfigs
                        form={form}
                        areaIndex={areaIndex}
                        enabledTypes={['area', 'unit', 'state']}
                        notesPlaceholder="County blend, data source, caveats..."
                      />
                      <div className={styles.sectionDivider}>
                        <TaxRatesTable areaIndex={areaIndex} />
                      </div>
                    </div>
                  );
                }}
              </form.Subscribe>
            )}
          </ScenarioFormLayout>
        );
      }}
    </form.Subscribe>
  );
}
